/**
 * @description PTO Request management list for admin/manager dashboard and employee view
 *
 * Key features:
 * - Filter by status, employee, date range
 * - Approve/deny pending requests with notes (managers/admins)
 * - View request details and balance info
 * - Tab navigation: Pending | All Requests | Balances
 * - Permission-aware: Employees see only their own data with simplified UI
 *
 * @dependencies
 * - timeClockService: PTO API calls
 * - PERMISSIONS: Permission constants
 * - PermissionGate: RBAC UI gating
 * - usePermissions: Permission check hook
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Check, X, Calendar, AlertCircle, User, Edit2, XCircle } from 'lucide-react';
import timeClockService from '../../../services/api/timeClockService';
import PermissionGate from '../../shared/PermissionGate';
import { usePermissions } from '../../../contexts/PermissionContext';
import { PERMISSIONS } from '../../../constants/permissionMappings';

// Leave type display configuration
const LEAVE_TYPES = {
  vacation: { label: 'Vacation', bgColor: 'bg-blue-50', textColor: 'text-blue-700', borderColor: 'border-blue-200' },
  sick: { label: 'Sick', bgColor: 'bg-red-50', textColor: 'text-red-700', borderColor: 'border-red-200' },
  personal: { label: 'Personal', bgColor: 'bg-purple-50', textColor: 'text-purple-700', borderColor: 'border-purple-200' },
  bereavement: { label: 'Bereavement', bgColor: 'bg-gray-50', textColor: 'text-gray-700', borderColor: 'border-gray-200' },
  jury_duty: { label: 'Jury Duty', bgColor: 'bg-amber-50', textColor: 'text-amber-700', borderColor: 'border-amber-200' },
  other: { label: 'Other', bgColor: 'bg-slate-50', textColor: 'text-slate-700', borderColor: 'border-slate-200' },
};

// Status display configuration
const STATUS_CONFIG = {
  pending: { label: 'Pending', bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
  approved: { label: 'Approved', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  denied: { label: 'Denied', bgColor: 'bg-red-100', textColor: 'text-red-700' },
  cancelled: { label: 'Cancelled', bgColor: 'bg-gray-100', textColor: 'text-gray-500' },
  taken: { label: 'Taken', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
};

const PtoRequestList = ({ refreshKey = 0 }) => {
  const { hasPermission, isLoaded } = usePermissions();
  const [activeTab, setActiveTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [balances, setBalances] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine if user can view all employees' data (manager/admin) or just their own
  const canViewAll = useMemo(() => {
    return isLoaded && hasPermission(PERMISSIONS.pto?.view_all);
  }, [hasPermission, isLoaded]);

  // Check if user can approve/deny PTO requests
  const canApprovePto = useMemo(() => {
    return isLoaded && hasPermission(PERMISSIONS.scheduling?.approve_pto);
  }, [hasPermission, isLoaded]);

  // Review modal state
  const [reviewingRequest, setReviewingRequest] = useState(null);
  const [reviewAction, setReviewAction] = useState(null); // 'approve' or 'deny'
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    leaveType: '',
    startDate: '',
    endDate: '',
  });

  // Balance adjustment modal state
  const [adjustingBalance, setAdjustingBalance] = useState(null);
  const [adjustmentHours, setAdjustmentHours] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjustmentSubmitting, setAdjustmentSubmitting] = useState(false);

  // Cancel request modal state
  const [cancellingRequest, setCancellingRequest] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const loadPendingCount = useCallback(async () => {
    try {
      const result = await timeClockService.getPtoRequests({ status: 'pending' });
      setPendingCount((result.requests || []).length);
    } catch (err) {
      // Non-fatal — badge is advisory
      console.error('Failed to load pending PTO count:', err);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (activeTab === 'pending') {
        params.status = 'pending';
      } else if (filters.status) {
        params.status = filters.status;
      }
      if (filters.leaveType) params.leaveType = filters.leaveType;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const result = await timeClockService.getPtoRequests(params);
      setRequests(result.requests || []);
      if (activeTab === 'pending') {
        setPendingCount((result.requests || []).length);
      }
    } catch (err) {
      console.error('Failed to load PTO requests:', err);
      setError('Failed to load PTO requests');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters]);

  const loadBalances = useCallback(async () => {
    if (activeTab !== 'balances') return;

    setLoading(true);
    setError(null);
    try {
      const result = await timeClockService.getAllPtoBalances();
      setBalances(result.balances || []);
    } catch (err) {
      console.error('Failed to load PTO balances:', err);
      setError('Failed to load PTO balances');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'balances') {
      loadBalances();
      loadPendingCount();
    } else {
      loadRequests();
      if (activeTab !== 'pending') {
        loadPendingCount();
      }
    }
  }, [activeTab, loadRequests, loadBalances, loadPendingCount, refreshKey]);

  // Handle approve/deny
  const handleReview = async () => {
    if (!reviewingRequest || !reviewAction) return;
    if (reviewAction === 'deny' && !reviewNotes.trim()) {
      setError('Please provide a reason for denial');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      if (reviewAction === 'approve') {
        await timeClockService.approvePtoRequest(reviewingRequest.id, reviewNotes || null);
      } else {
        await timeClockService.denyPtoRequest(reviewingRequest.id, reviewNotes);
      }
      setReviewingRequest(null);
      setReviewAction(null);
      setReviewNotes('');
      loadRequests();
      loadPendingCount();
    } catch (err) {
      console.error('Failed to review request:', err);
      setError(err.response?.data?.error || 'Failed to process request');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle cancel request
  const handleCancelRequest = async () => {
    if (!cancellingRequest) return;

    setCancelSubmitting(true);
    setError(null);
    try {
      await timeClockService.cancelPtoRequest(cancellingRequest.id, cancelReason || null);
      setCancellingRequest(null);
      setCancelReason('');
      loadRequests();
    } catch (err) {
      console.error('Failed to cancel request:', err);
      setError(err.response?.data?.error || 'Failed to cancel request');
    } finally {
      setCancelSubmitting(false);
    }
  };

  // Handle balance adjustment submission
  const handleAdjustBalance = async () => {
    if (!adjustingBalance || adjustmentHours === '') return;

    const newHours = parseFloat(adjustmentHours);
    if (isNaN(newHours) || newHours < 0) {
      setError('Please enter a valid number of hours');
      return;
    }

    setAdjustmentSubmitting(true);
    setError(null);
    try {
      await timeClockService.adjustPtoBalance(
        adjustingBalance.employeeId,
        adjustingBalance.balanceType,
        {
          allocatedHours: newHours,
          reason: adjustmentReason || undefined,
        }
      );
      setAdjustingBalance(null);
      setAdjustmentHours('');
      setAdjustmentReason('');
      loadBalances();
    } catch (err) {
      console.error('Failed to adjust balance:', err);
      setError(err.response?.data?.error || 'Failed to adjust balance');
    } finally {
      setAdjustmentSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (isoStr) => {
    if (!isoStr) return '—';
    return new Date(isoStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Tabs adapt based on permission level
  // Employees: My Pending | My Requests | My Balances
  // Managers/Admins: Pending | All Requests | Balances
  const tabs = useMemo(() => {
    const pendingCount = requests.filter(r => r.status === 'pending').length;
    if (canViewAll) {
      return [
        { key: 'pending', label: 'Pending', count: pendingCount },
        { key: 'all', label: 'All Requests' },
        { key: 'balances', label: 'Balances' },
      ];
    }
    // Employee view - simplified labels indicating "my" data
    return [
      { key: 'pending', label: 'My Pending', count: pendingCount },
      { key: 'all', label: 'My Requests' },
      { key: 'balances', label: 'My Balances' },
    ];
  }, [canViewAll, requests]);

  // Group balances by employee for display
  // Note: Backend returns snake_case, frontend normalizes to camelCase
  const groupedBalances = balances.reduce((acc, balance) => {
    const key = balance.employee_id;
    if (!acc[key]) {
      acc[key] = {
        employeeId: balance.employee_id,
        employeeName: balance.employee_name,
        employeeEmail: balance.employee_email,
        balances: [],
      };
    }
    acc[key].balances.push({
      id: balance.id,
      balanceType: balance.balance_type,
      allocatedHours: balance.allocated_hours,
      usedHours: balance.used_hours,
      pendingHours: balance.pending_hours,
      availableHours: balance.available_hours,
    });
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {/* Review Modal */}
      {reviewingRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {reviewAction === 'approve' ? 'Approve' : 'Deny'} Request
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {reviewingRequest.employeeName}'s {LEAVE_TYPES[reviewingRequest.leaveType]?.label.toLowerCase() || 'time off'} request
              for {reviewingRequest.hoursRequested}h on {formatDate(reviewingRequest.startDate)}
              {reviewingRequest.startDate !== reviewingRequest.endDate && ` — ${formatDate(reviewingRequest.endDate)}`}
            </p>

            {error && (
              <div className="mb-4 flex items-center gap-2 text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes {reviewAction === 'deny' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                rows={3}
                placeholder={reviewAction === 'deny' ? 'Reason for denial (required)' : 'Optional notes...'}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand focus:border-brand outline-none resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setReviewingRequest(null);
                  setReviewAction(null);
                  setReviewNotes('');
                  setError(null);
                }}
                disabled={submitting}
                className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReview}
                disabled={submitting}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                  reviewAction === 'approve'
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                {submitting ? 'Processing...' : reviewAction === 'approve' ? 'Approve' : 'Deny'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Balance Adjustment Modal */}
      {adjustingBalance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Adjust PTO Balance
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Adjusting {LEAVE_TYPES[adjustingBalance.balanceType]?.label || adjustingBalance.balanceType} balance for{' '}
              <span className="font-medium">{adjustingBalance.employeeName}</span>
            </p>

            {error && (
              <div className="mb-4 flex items-center gap-2 text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div className="mb-4">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>Current Allocated:</span>
                <span className="font-medium">{adjustingBalance.allocatedHours}h</span>
              </div>
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>Used:</span>
                <span className="font-medium">{adjustingBalance.usedHours}h</span>
              </div>
              <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                <span>Available:</span>
                <span className="font-medium text-green-600">{adjustingBalance.availableHours}h</span>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Allocated Hours <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={adjustmentHours}
                onChange={(e) => setAdjustmentHours(e.target.value)}
                placeholder={adjustingBalance.allocatedHours.toString()}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand focus:border-brand outline-none"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason (Optional)
              </label>
              <textarea
                value={adjustmentReason}
                onChange={(e) => setAdjustmentReason(e.target.value)}
                rows={2}
                placeholder="e.g., Annual adjustment, Manual correction..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand focus:border-brand outline-none resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setAdjustingBalance(null);
                  setAdjustmentHours('');
                  setAdjustmentReason('');
                  setError(null);
                }}
                disabled={adjustmentSubmitting}
                className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAdjustBalance}
                disabled={adjustmentSubmitting || adjustmentHours === ''}
                className="flex-1 py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium transition-colors disabled:opacity-50"
              >
                {adjustmentSubmitting ? 'Saving...' : 'Save Adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Request Modal */}
      {cancellingRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Cancel PTO Request
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to cancel your {LEAVE_TYPES[cancellingRequest.leaveType]?.label.toLowerCase() || 'time off'} request
              for {cancellingRequest.hoursRequested}h on {formatDate(cancellingRequest.startDate)}
              {cancellingRequest.startDate !== cancellingRequest.endDate && ` — ${formatDate(cancellingRequest.endDate)}`}?
              {cancellingRequest.status === 'approved' && (
                <span className="block mt-2 text-amber-600 font-medium">
                  This request was already approved. The hours will be returned to your balance.
                </span>
              )}
            </p>

            {error && (
              <div className="mb-4 flex items-center gap-2 text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason (Optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={2}
                placeholder="e.g., Plans changed, Rescheduling..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand focus:border-brand outline-none resize-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setCancellingRequest(null);
                  setCancelReason('');
                  setError(null);
                }}
                disabled={cancelSubmitting}
                className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50"
              >
                Keep Request
              </button>
              <button
                onClick={handleCancelRequest}
                disabled={cancelSubmitting}
                className="flex-1 py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium transition-colors disabled:opacity-50"
              >
                {cancelSubmitting ? 'Cancelling...' : 'Cancel Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 bg-white p-1.5 rounded-xl border border-gray-200 shadow-sm">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`
              flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200
              ${activeTab === tab.key
                ? 'bg-gray-50 text-gray-900 shadow-sm border border-gray-200 ring-1 ring-black/5'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50/50'
              }
            `}
          >
            {tab.label}
            {tab.key === 'pending' && activeTab !== 'pending' && pendingCount > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters (for All Requests tab) */}
      {activeTab === 'all' && (
        <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium focus:ring-2 focus:ring-brand focus:border-brand"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
            <option value="cancelled">Cancelled</option>
            <option value="taken">Taken</option>
          </select>

          <select
            value={filters.leaveType}
            onChange={(e) => setFilters({ ...filters, leaveType: e.target.value })}
            className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium focus:ring-2 focus:ring-brand focus:border-brand"
          >
            <option value="">All Types</option>
            {Object.entries(LEAVE_TYPES).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>

          <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 p-1">
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="bg-transparent border-none focus:ring-0 text-sm px-2 py-1"
            />
            <span className="text-gray-400 text-xs font-bold px-1">TO</span>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="bg-transparent border-none focus:ring-0 text-sm px-2 py-1"
            />
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && !reviewingRequest && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-lg border border-red-200">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Content */}
      {activeTab === 'balances' ? (
        // Balances View - adapts for employee vs manager/admin view
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <div className="animate-spin w-8 h-8 border-2 border-brand border-t-transparent rounded-full mb-3" />
              <p className="text-sm font-medium">Loading balances...</p>
            </div>
          ) : Object.keys(groupedBalances).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <User className="w-8 h-8 mb-2 text-gray-300" />
              <p className="font-medium">{canViewAll ? 'No PTO balances found' : 'No PTO balances available'}</p>
            </div>
          ) : !canViewAll ? (
            // Employee view - simplified card layout for their own balances
            <div className="p-6">
              <div className="flex flex-wrap gap-4 justify-center">
                {Object.values(groupedBalances).flatMap(employee =>
                  employee.balances.map((balance) => {
                    const typeConfig = LEAVE_TYPES[balance.balanceType] || LEAVE_TYPES.other;
                    return (
                      <div key={balance.id} className={`px-6 py-4 rounded-xl border-2 ${typeConfig.bgColor} ${typeConfig.borderColor} min-w-[160px]`}>
                        <p className={`text-sm font-bold ${typeConfig.textColor} uppercase mb-1`}>{typeConfig.label}</p>
                        <p className="text-3xl font-bold text-gray-900">
                          {parseFloat(balance.availableHours || 0)}h
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          of {parseFloat(balance.allocatedHours || 0)}h allocated
                        </p>
                        {parseFloat(balance.usedHours || 0) > 0 && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {parseFloat(balance.usedHours || 0)}h used
                          </p>
                        )}
                        {parseFloat(balance.pendingHours || 0) > 0 && (
                          <p className="text-xs text-amber-600 font-medium mt-0.5">
                            {parseFloat(balance.pendingHours || 0)}h pending
                          </p>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ) : (
            // Manager/Admin view - list all employees
            <div className="divide-y divide-gray-100">
              {Object.values(groupedBalances).map((employee) => (
                <div key={employee.employeeId} className="p-4 hover:bg-gray-50/50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-orange-50 text-orange-700 flex items-center justify-center text-sm font-bold border border-orange-100">
                        {employee.employeeName?.split(' ').map(n => n[0]).join('') || '?'}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">{employee.employeeName}</p>
                        <p className="text-sm text-gray-500">{employee.employeeEmail}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {employee.balances.map((balance) => {
                        const typeConfig = LEAVE_TYPES[balance.balanceType] || LEAVE_TYPES.other;
                        return (
                          <div key={balance.id} className={`px-3 py-2 rounded-lg border ${typeConfig.bgColor} ${typeConfig.borderColor} relative group`}>
                            <p className={`text-xs font-bold ${typeConfig.textColor} uppercase`}>{typeConfig.label}</p>
                            <p className="text-lg font-bold text-gray-900">
                              {parseFloat(balance.availableHours || 0)}h
                              <span className="text-xs font-normal text-gray-500 ml-1">
                                / {parseFloat(balance.allocatedHours || 0)}h
                              </span>
                            </p>
                            {parseFloat(balance.pendingHours || 0) > 0 && (
                              <p className="text-xs text-amber-600">{balance.pendingHours}h pending</p>
                            )}
                            <PermissionGate permission={PERMISSIONS.pto?.manage}>
                              <button
                                onClick={() => {
                                  setAdjustingBalance({
                                    employeeId: employee.employeeId,
                                    employeeName: employee.employeeName,
                                    balanceType: balance.balanceType,
                                    allocatedHours: parseFloat(balance.allocatedHours || 0),
                                    usedHours: parseFloat(balance.usedHours || 0),
                                    availableHours: parseFloat(balance.availableHours || 0),
                                  });
                                  setAdjustmentHours(balance.allocatedHours?.toString() || '0');
                                  setError(null);
                                }}
                                className="absolute -top-2 -right-2 p-1 bg-white rounded-full shadow-md border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Adjust balance"
                              >
                                <Edit2 className="w-3 h-3 text-gray-500" />
                              </button>
                            </PermissionGate>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        // Requests Table
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-100">
                  {/* Employee column only shown for managers/admins who can view all */}
                  {canViewAll && (
                    <th className="text-left px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Employee</th>
                  )}
                  <th className="text-left px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Type</th>
                  <th className="text-left px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Dates</th>
                  <th className="text-left px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Hours</th>
                  <th className="text-left px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Status</th>
                  <th className="text-left px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Submitted</th>
                  <th className="text-right px-6 py-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan={6 + (canViewAll ? 1 : 0)} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <div className="animate-spin w-8 h-8 border-2 border-brand border-t-transparent rounded-full" />
                        <p className="text-sm font-medium">Loading requests...</p>
                      </div>
                    </td>
                  </tr>
                ) : requests.length === 0 ? (
                  <tr>
                    <td colSpan={6 + (canViewAll ? 1 : 0)} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <Calendar className="w-8 h-8 text-gray-300" />
                        <p className="font-medium">
                          {activeTab === 'pending'
                            ? (canViewAll ? 'No pending requests' : 'No pending requests')
                            : (canViewAll ? 'No PTO requests found' : 'No requests found')}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  requests.map((request) => {
                    const typeConfig = LEAVE_TYPES[request.leaveType] || LEAVE_TYPES.other;
                    const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;

                    return (
                      <tr key={request.id} className="hover:bg-gray-50/50 transition-colors">
                        {/* Employee column only for managers/admins */}
                        {canViewAll && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-orange-50 text-orange-700 flex items-center justify-center text-xs font-bold border border-orange-100">
                                {request.employeeName?.split(' ').map(n => n[0]).join('') || '?'}
                              </div>
                              <div>
                                <p className="text-sm font-bold text-gray-900">{request.employeeName || 'Unknown'}</p>
                                {request.employeeEmail && (
                                  <p className="text-xs text-gray-500">{request.employeeEmail}</p>
                                )}
                              </div>
                            </div>
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tight ${typeConfig.bgColor} ${typeConfig.textColor}`}>
                            {typeConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm font-medium text-gray-900">{formatDate(request.startDate)}</p>
                          {request.startDate !== request.endDate && (
                            <p className="text-xs text-gray-500">to {formatDate(request.endDate)}</p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                          {request.hoursRequested}h
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tight ${statusConfig.bgColor} ${statusConfig.textColor}`}>
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDateTime(request.createdAt)}
                        </td>
                        {/* Actions column - approve/deny for managers, cancel for own requests */}
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Approve/Deny buttons for managers on pending requests */}
                            {canApprovePto && request.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => {
                                    setReviewingRequest(request);
                                    setReviewAction('approve');
                                    setError(null);
                                  }}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-all"
                                  title="Approve"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setReviewingRequest(request);
                                    setReviewAction('deny');
                                    setError(null);
                                  }}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                  title="Deny"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {/* Cancel button for own pending or approved requests (employees view) */}
                            {!canViewAll && ['pending', 'approved'].includes(request.status) && (
                              <button
                                onClick={() => {
                                  setCancellingRequest(request);
                                  setError(null);
                                }}
                                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                title="Cancel request"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            )}
                            {/* Show reviewer info for reviewed requests (managers view) */}
                            {canApprovePto && request.status !== 'pending' && request.reviewerName && (
                              <span className="text-xs text-gray-400">
                                by {request.reviewerName}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PtoRequestList;
